import React, { useEffect, useRef, useState } from 'react';
import { LanguageCode } from '../types';
import { COZY_SLEEPING_MOON } from '../constants';

type Activity = {
  prompt: string;
  options: string[];
  answer: number;
};

type CompanionCopy = {
  eyebrow: string;
  title: string;
  description: string;
  storyPhase: string;
  artPhase: string;
  voicePhase: string;
  lumiMessage: (name: string) => string;
  correct: string;
  retry: string;
  activities: Activity[];
};

// These are intentionally local, calm activities. They never make an AI
// request, do not collect any child data and cannot make story generation
// slower. A short, familiar pattern is kinder at bedtime than a busy game.
const COPY: Record<LanguageCode, CompanionCopy> = {
  de: {
    eyebrow: 'KLEINE STERNENPAUSE', title: 'Lumi bereitet dein Buch vor',
    description: 'Damit dein Bilderbuch wirklich schön und stimmig wird, malt Lumi jede Szene mit passenden Details sorgfältig fertig. Bis dahin gibt es eine kleine, ruhige Aufgabe.',
    storyPhase: 'Die Geschichte wird vorbereitet …', artPhase: 'Die Bilder werden liebevoll gemalt …', voicePhase: 'Lumi bereitet die Erzählerstimme vor …',
    lumiMessage: (name) => `Hallo ${name || 'du'}! Ich bin Lumi. Bleib kurz bei mir – ich passe auf, dass dein Bilderbuch, die Bilder und meine Erzählerstimme ganz fertig sind.`,
    correct: 'Wunderbar! Ein Stern für dich. ✨', retry: 'Fast – schau noch einmal ganz genau.',
    activities: [
      { prompt: 'Was leuchtet nachts am Himmel?', options: ['☀️', '🌙', '☁️'], answer: 1 },
      { prompt: 'Was kommt als Nächstes?  ⭐  🌙  ⭐  🌙', options: ['⭐', '🌙', '☁️'], answer: 0 },
      { prompt: 'Wie viele Sterne siehst du?  ⭐ ⭐ ⭐', options: ['2', '3', '4'], answer: 1 },
    ],
  },
  en: {
    eyebrow: 'A LITTLE STAR PAUSE', title: 'Lumi is preparing your book',
    description: 'To make your picture book beautiful and consistent, Lumi carefully paints every scene with the right details. Until then, enjoy a calm little activity.',
    storyPhase: 'The story is being prepared …', artPhase: 'The pictures are being painted with care …', voicePhase: 'Lumi is preparing the storyteller voice …',
    lumiMessage: (name) => `Hello ${name || 'there'}! I’m Lumi. Stay with me for a moment — I’m making sure your picture book, pictures and storyteller voice are completely ready.`,
    correct: 'Wonderful! A star for you. ✨', retry: 'Almost — take another close look.',
    activities: [
      { prompt: 'What shines in the night sky?', options: ['☀️', '🌙', '☁️'], answer: 1 },
      { prompt: 'What comes next?  ⭐  🌙  ⭐  🌙', options: ['⭐', '🌙', '☁️'], answer: 0 },
      { prompt: 'How many stars can you see?  ⭐ ⭐ ⭐', options: ['2', '3', '4'], answer: 1 },
    ],
  },
  fr: {
    eyebrow: 'PETITE PAUSE ÉTOILÉE', title: 'Lumi prépare ton livre',
    description: 'Pour que ton livre illustré soit beau et cohérent, Lumi peint chaque scène avec soin et les bons détails. En attendant, voici une petite activité calme.',
    storyPhase: 'L’histoire est en préparation …', artPhase: 'Les images sont peintes avec soin …', voicePhase: 'Lumi prépare la voix de narration …',
    lumiMessage: (name) => `Bonjour ${name || 'toi'} ! Je suis Lumi. Reste un petit moment avec moi : je veille à ce que ton livre, les images et la voix soient complètement prêts.`,
    correct: 'Bravo ! Une étoile pour toi. ✨', retry: 'Presque — regarde encore une fois.',
    activities: [
      { prompt: 'Qu’est-ce qui brille dans le ciel la nuit ?', options: ['☀️', '🌙', '☁️'], answer: 1 },
      { prompt: 'Que vient ensuite ?  ⭐  🌙  ⭐  🌙', options: ['⭐', '🌙', '☁️'], answer: 0 },
      { prompt: 'Combien d’étoiles vois-tu ?  ⭐ ⭐ ⭐', options: ['2', '3', '4'], answer: 1 },
    ],
  },
  es: {
    eyebrow: 'PEQUEÑA PAUSA DE ESTRELLAS', title: 'Lumi prepara tu libro',
    description: 'Para que tu libro ilustrado sea bonito y coherente, Lumi pinta cada escena con cuidado y con los detalles adecuados. Mientras tanto, hay una actividad tranquila.',
    storyPhase: 'La historia se está preparando …', artPhase: 'Las imágenes se están pintando con cariño …', voicePhase: 'Lumi prepara la voz narradora …',
    lumiMessage: (name) => `¡Hola ${name || 'amigo'}! Soy Lumi. Quédate conmigo un momento: me aseguro de que tu libro, las imágenes y la voz narradora estén completamente listos.`,
    correct: '¡Genial! Una estrella para ti. ✨', retry: 'Casi — mira otra vez con atención.',
    activities: [
      { prompt: '¿Qué brilla en el cielo de noche?', options: ['☀️', '🌙', '☁️'], answer: 1 },
      { prompt: '¿Qué viene después?  ⭐  🌙  ⭐  🌙', options: ['⭐', '🌙', '☁️'], answer: 0 },
      { prompt: '¿Cuántas estrellas ves?  ⭐ ⭐ ⭐', options: ['2', '3', '4'], answer: 1 },
    ],
  },
  zh: {
    eyebrow: '小小星星休息', title: 'Lumi 正在准备你的故事书',
    description: '为了让绘本精致又连贯，Lumi 正在认真绘制每一幕和相应细节。等待时，可以做一个安静的小活动。',
    storyPhase: '故事正在准备中 …', artPhase: '图画正在被细心绘制 …', voicePhase: 'Lumi 正在准备讲故事的声音 …',
    lumiMessage: (name) => `你好，${name || '小朋友'}！我是 Lumi。陪我等一会儿吧，我会确保你的绘本、图画和讲故事的声音全都准备好。`,
    correct: '太棒了！送你一颗星星。✨', retry: '差一点——再仔细看看。',
    activities: [
      { prompt: '夜晚天空中什么会发光？', options: ['☀️', '🌙', '☁️'], answer: 1 },
      { prompt: '接下来是什么？  ⭐  🌙  ⭐  🌙', options: ['⭐', '🌙', '☁️'], answer: 0 },
      { prompt: '你看到几颗星星？  ⭐ ⭐ ⭐', options: ['2', '3', '4'], answer: 1 },
    ],
  },
  ja: {
    eyebrow: 'ちいさな星の休けい', title: 'Lumi が絵本を準備しています',
    description: 'きれいで一貫した絵本になるように、Lumi が場面ごとの細かな絵を丁寧に描いています。待っている間は、静かなミニゲームをどうぞ。',
    storyPhase: 'おはなしを準備しています …', artPhase: '絵を心をこめて描いています …', voicePhase: 'Lumi が読み聞かせの声を準備しています …',
    lumiMessage: (name) => `こんにちは、${name || 'おともだち'}！Lumi だよ。少しだけ一緒に待ってね。絵本も絵も読み聞かせの声も、全部そろうように見守っているよ。`,
    correct: 'すてき！星をひとつあげるね。✨', retry: 'おしい！もう一度よく見てみよう。',
    activities: [
      { prompt: '夜の空で光るのはどれ？', options: ['☀️', '🌙', '☁️'], answer: 1 },
      { prompt: 'つぎはどれ？  ⭐  🌙  ⭐  🌙', options: ['⭐', '🌙', '☁️'], answer: 0 },
      { prompt: '星はいくつ見える？  ⭐ ⭐ ⭐', options: ['2', '3', '4'], answer: 1 },
    ],
  },
  it: {
    eyebrow: 'PICCOLA PAUSA TRA LE STELLE', title: 'Lumi prepara il tuo libro',
    description: 'Per rendere il tuo libro illustrato bello e coerente, Lumi dipinge ogni scena con i dettagli giusti. Nel frattempo, ecco una piccola attività tranquilla.',
    storyPhase: 'La storia si sta preparando …', artPhase: 'Le immagini vengono dipinte con cura …', voicePhase: 'Lumi prepara la voce narrante …',
    lumiMessage: (name) => `Ciao ${name || 'amico'}! Sono Lumi. Resta un attimo con me: mi assicuro che il tuo libro, le immagini e la voce narrante siano pronti.`,
    correct: 'Benissimo! Una stella per te. ✨', retry: 'Quasi — guarda ancora con attenzione.',
    activities: [
      { prompt: 'Cosa brilla nel cielo di notte?', options: ['☀️', '🌙', '☁️'], answer: 1 },
      { prompt: 'Cosa viene dopo?  ⭐  🌙  ⭐  🌙', options: ['⭐', '🌙', '☁️'], answer: 0 },
      { prompt: 'Quante stelle vedi?  ⭐ ⭐ ⭐', options: ['2', '3', '4'], answer: 1 },
    ],
  },
  ar: {
    eyebrow: 'استراحة نجوم صغيرة', title: 'لومي تحضّر كتابك',
    description: 'لكي يكون كتابك المصوّر جميلًا ومتناسقًا، ترسم لومي كل مشهد بعناية وبالتفاصيل المناسبة. وحتى ذلك الحين، إليك نشاط صغير وهادئ.',
    storyPhase: 'يجري تحضير القصة …', artPhase: 'يجري رسم الصور بعناية …', voicePhase: 'لومي تُحضّر صوت الراوي …',
    lumiMessage: (name) => `مرحبًا ${name || 'يا صديقي'}! أنا لومي. ابقَ معي قليلًا، وسأتأكد من أن كتابك المصوّر والصور وصوت الراوي جاهزة تمامًا.`,
    correct: 'رائع! نجمة لك. ✨', retry: 'قريب جدًا — انظر مرة أخرى جيدًا.',
    activities: [
      { prompt: 'ما الذي يضيء في السماء ليلًا؟', options: ['☀️', '🌙', '☁️'], answer: 1 },
      { prompt: 'ما التالي؟  ⭐  🌙  ⭐  🌙', options: ['⭐', '🌙', '☁️'], answer: 0 },
      { prompt: 'كم نجمة تراها؟  ⭐ ⭐ ⭐', options: ['2', '3', '4'], answer: 1 },
    ],
  },
  hi: {
    eyebrow: 'नन्हा सितारा विराम', title: 'लुमी तुम्हारी किताब तैयार कर रही है',
    description: 'तुम्हारी चित्र-पुस्तक सुंदर और एक जैसी लगे, इसलिए लुमी हर दृश्य को सही विवरणों के साथ ध्यान से बना रही है। तब तक एक छोटा शांत खेल खेलो।',
    storyPhase: 'कहानी तैयार हो रही है …', artPhase: 'चित्र प्यार से बनाए जा रहे हैं …', voicePhase: 'लुमी कहानी सुनाने वाली आवाज़ तैयार कर रही है …',
    lumiMessage: (name) => `नमस्ते ${name || 'दोस्त'}! मैं लुमी हूँ। मेरे साथ थोड़ा रुको — मैं देख रही हूँ कि तुम्हारी चित्र-पुस्तक, चित्र और कहानी सुनाने वाली आवाज़ पूरी तरह तैयार हों।`,
    correct: 'बहुत बढ़िया! तुम्हारे लिए एक तारा। ✨', retry: 'लगभग — एक बार और ध्यान से देखो।',
    activities: [
      { prompt: 'रात के आकाश में क्या चमकता है?', options: ['☀️', '🌙', '☁️'], answer: 1 },
      { prompt: 'आगे क्या आएगा?  ⭐  🌙  ⭐  🌙', options: ['⭐', '🌙', '☁️'], answer: 0 },
      { prompt: 'तुम्हें कितने तारे दिखते हैं?  ⭐ ⭐ ⭐', options: ['2', '3', '4'], answer: 1 },
    ],
  },
};

// These extra puzzles work in every language because they use patterns,
// colours and symbols rather than a language-specific hint. They keep the
// waiting state varied without calling an AI service or slowing the story.
const EXTRA_CALM_ACTIVITIES: Activity[] = [
  { prompt: '🌙  ⭐  🌙  ⭐  ?', options: ['🌙', '⭐', '☁️'], answer: 0 },
  { prompt: '△  ○  □', options: ['△', '○', '□'], answer: 0 },
  { prompt: '☁️  ☁️  ☁️  ☁️', options: ['3', '4', '5'], answer: 1 },
  { prompt: '🔵  +  🟡  =  ?', options: ['🟢', '🟣', '🟠'], answer: 0 },
  { prompt: '⭐  ⭐  🌙', options: ['⭐  ⭐  🌙', '☁️  ⭐  ☀️', '🌙  ☀️  ⭐'], answer: 0 },
];

export default function WeavingCompanion({
  language,
  childName,
  stage,
}: {
  language: LanguageCode;
  childName: string;
  stage: 'STORY' | 'PAINTING' | 'VOICE';
}) {
  const copy = COPY[language] || COPY.en;
  const activities = [...copy.activities, ...EXTRA_CALM_ACTIVITIES];
  const [activityIndex, setActivityIndex] = useState(0);
  const [result, setResult] = useState<'correct' | 'retry' | null>(null);
  const nextTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const moveToNext = () => {
    if (nextTimer.current) window.clearTimeout(nextTimer.current);
    setResult(null);
    setActivityIndex((current) => (current + 1) % activities.length);
  };

  useEffect(() => {
    setActivityIndex(0);
    setResult(null);
  }, [language]);

  useEffect(() => {
    const timer = window.setInterval(moveToNext, 12_000);
    return () => {
      window.clearInterval(timer);
      if (nextTimer.current) window.clearTimeout(nextTimer.current);
    };
  }, [language]);

  const activity = activities[activityIndex];
  const choose = (index: number) => {
    const isCorrect = index === activity.answer;
    setResult(isCorrect ? 'correct' : 'retry');
    if (isCorrect) {
      nextTimer.current = window.setTimeout(moveToNext, 1100);
    }
  };

  const phase = stage === 'PAINTING' ? copy.artPhase : stage === 'VOICE' ? copy.voicePhase : copy.storyPhase;

  return (
    <section className="w-full max-w-md rounded-[28px] border border-white/15 bg-slate-950/35 p-5 text-left shadow-[0_18px_70px_rgba(0,0,0,0.24)] backdrop-blur-xl sm:p-6" aria-live="polite">
      <div className="lumi-broadcast" aria-hidden="true">
        <div className="lumi-broadcast__cloud">
          <span className="lumi-broadcast__puff lumi-broadcast__puff--one" />
          <span className="lumi-broadcast__puff lumi-broadcast__puff--two" />
          <span className="lumi-broadcast__puff lumi-broadcast__puff--three" />
          <span className="lumi-broadcast__base" />
          <span className="lumi-broadcast__spark lumi-broadcast__spark--one">✦</span>
          <span className="lumi-broadcast__spark lumi-broadcast__spark--two">✦</span>
          <div className="lumi-broadcast__tv">
            <span className="lumi-broadcast__antenna lumi-broadcast__antenna--left" />
            <span className="lumi-broadcast__antenna lumi-broadcast__antenna--right" />
            <div className="lumi-broadcast__screen"><div className="h-11 w-11">{COZY_SLEEPING_MOON}</div></div>
            <span className="lumi-broadcast__knob" />
            <span className="lumi-broadcast__light" />
          </div>
        </div>
      </div>
      <div className="mt-2 text-center">
        <p className="text-[8px] font-black tracking-[0.22em] text-amber-100/75">{copy.eyebrow}</p>
        <h3 className="mt-1 text-lg font-bold dream-font text-white">{copy.title}</h3>
        <p className="mt-3 text-xs leading-relaxed text-white/80">{copy.lumiMessage(childName)}</p>
        <p className="mt-3 text-[10px] font-bold text-cyan-100/85">{phase}</p>
      </div>

      <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.055] p-4">
        <p className="mb-2 text-center text-[9px] font-black tracking-[0.18em] text-amber-100/65">{activityIndex + 1} / {activities.length}</p>
        <p className="min-h-10 text-center text-sm font-semibold leading-relaxed text-white">{activity.prompt}</p>
        <div className="mt-4 grid grid-cols-3 gap-2">
          {activity.options.map((option, index) => (
            <button
              key={`${activityIndex}-${option}-${index}`}
              type="button"
              onClick={() => choose(index)}
              className="min-h-12 rounded-xl border border-white/15 bg-white/10 px-2 text-lg font-bold text-white transition active:scale-95 hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-200"
              aria-label={option}
            >
              {option}
            </button>
          ))}
        </div>
        <p className={`mt-3 min-h-4 text-center text-[11px] font-bold transition ${result === 'correct' ? 'text-emerald-200' : result === 'retry' ? 'text-amber-100' : 'text-transparent'}`}>
          {result === 'correct' ? copy.correct : result === 'retry' ? copy.retry : '–'}
        </p>
      </div>
    </section>
  );
}
